import React from 'react';
import {
    FormControlLabel,
    Grid,
    MenuItem,
    Radio,
    RadioGroup,
    TextField,
} from '@material-ui/core';
import {KeyboardDatePicker} from '@material-ui/pickers';
import {TFunction} from 'i18next';
import MedicinalProductAutocomplete from '../MedicinalProductAutocomplete/MedicinalProductAutocomplete';
import MedicationPackSelect from '../MedicationPackSelect/MedicationPackSelect';
import ReimbursementSelect from '../ReimbursementSelect/ReimbursementSelect';
import {
    MedicationPackDosageUnit,
    MedicationPackDto,
    MedicationPackReimbursement,
} from '../../../types/prescription';
import {
    DOSAGE_MODES,
    DOSAGE_FREQUENCY_OPTIONS,
    DosageModeOption,
} from '../../../validators/newPrescription';

export type PrescriptionFormValues = {
    dosageMode: DosageModeOption;
    medicinalProduct: any;
    medicationPackId: number | string;
    paymentLevel: string;
    effectiveDate: Date;
    quantity: number;
    treatmentDuration: number | string;
    dosageFrequencyOption: string;
    dosageIntervalHours: number | string;
    dosageAmount: number | string;
    dosageUnit: string;
    usageRecommendation: string;
};

type PrescriptionFormFieldsProps = {
    t: TFunction;
    formik: any;
    medicationPacks: MedicationPackDto[];
    handleChangeMedicationPack: (
        event: React.ChangeEvent<{
            name?: string;
            value: unknown;
        }>,
    ) => void;
    hasPredefinedDosageUnits: boolean;
    medicationPackDosageUnits: MedicationPackDosageUnit[];
    isReimbursementDisabled: boolean;
    medicationPackReimbursements?: MedicationPackReimbursement[];
    insuranceInfoComponent?: React.ReactNode;
    onChangeDosageMode: (value: DosageModeOption) => void;
};

const PrescriptionFormFields = ({
    t,
    formik,
    medicationPacks,
    handleChangeMedicationPack,
    hasPredefinedDosageUnits,
    medicationPackDosageUnits,
    isReimbursementDisabled,
    medicationPackReimbursements,
    insuranceInfoComponent,
    onChangeDosageMode,
}: PrescriptionFormFieldsProps) => (
    <>
        <Grid item xs={12}>
            <MedicinalProductAutocomplete
                onChange={newValue =>
                    formik.setFieldValue('medicinalProduct', newValue)
                }
                medicinalProduct={formik.values.medicinalProduct}
                error={
                    formik.touched.medicinalProduct &&
                    Boolean(formik.errors.medicinalProduct)
                }
                helperText={
                    formik.touched.medicinalProduct &&
                    formik.errors.medicinalProduct
                }
            />
        </Grid>
        <Grid item xs={12}>
            <MedicationPackSelect
                medicationPacks={medicationPacks}
                medicationPackId={formik.values.medicationPackId}
                onChange={handleChangeMedicationPack}
                error={Boolean(
                    formik.touched.medicationPackId &&
                        formik.errors.medicationPackId,
                )}
                helperText={formik.errors.medicationPackId}
            />
        </Grid>
        <Grid item xs={12}>
            <KeyboardDatePicker
                disableToolbar
                variant="inline"
                format="dd.MM.yyyy"
                margin="normal"
                name="effectiveDate"
                id="effectiveDate"
                label={t('effectiveDate')}
                value={formik.values.effectiveDate}
                onChange={value =>
                    formik.setFieldValue('effectiveDate', value, true)
                }
                KeyboardButtonProps={{
                    'aria-label': 'change date',
                }}
                inputVariant="outlined"
                fullWidth
                minDate={new Date()}
                style={{marginTop: 0, marginBottom: 0}}
            />
        </Grid>
        <Grid item xs={12}>
            <TextField
                fullWidth
                type="number"
                label={t('quantity')}
                variant="outlined"
                name="quantity"
                value={formik.values.quantity}
                onChange={formik.handleChange}
                InputProps={{inputProps: {min: 1}}}
                helperText={
                    formik.touched.quantity && formik.errors.quantity
                }
                error={Boolean(
                    formik.touched.quantity && formik.errors.quantity,
                )}
            />
        </Grid>
        <Grid item xs={12}>
            <RadioGroup
                row
                aria-label="dosage-mode"
                name="dosageMode"
                value={formik.values.dosageMode}
                onChange={event =>
                    onChangeDosageMode(event.target.value as DosageModeOption)
                }
            >
                <FormControlLabel
                    value={DOSAGE_MODES.SIMPLIFIED}
                    control={<Radio size="small" />}
                    label={t('simplifiedDosage')}
                />
                <FormControlLabel
                    value={DOSAGE_MODES.EXTENDED}
                    control={<Radio size="small" />}
                    label={t('extendedDosage')}
                />
            </RadioGroup>
        </Grid>
        {formik.values.dosageMode === DOSAGE_MODES.SIMPLIFIED && (
            <Grid item xs={12}>
                <TextField
                    fullWidth
                    type="text"
                    label={t('usageRecommendation')}
                    variant="outlined"
                    name="usageRecommendation"
                    value={formik.values.usageRecommendation}
                    onChange={formik.handleChange}
                    helperText={
                        formik.touched.usageRecommendation &&
                        formik.errors.usageRecommendation
                    }
                    error={Boolean(
                        formik.touched.usageRecommendation &&
                            formik.errors.usageRecommendation,
                    )}
                />
            </Grid>
        )}
        {formik.values.dosageMode === DOSAGE_MODES.EXTENDED && (
            <>
                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        type="number"
                        label={t('treatmentDuration')}
                        variant="outlined"
                        name="treatmentDuration"
                        value={formik.values.treatmentDuration}
                        onChange={formik.handleChange}
                        InputProps={{inputProps: {min: 1, step: 1}}}
                        helperText={
                            formik.touched.treatmentDuration &&
                            formik.errors.treatmentDuration
                        }
                        error={Boolean(
                            formik.touched.treatmentDuration &&
                                formik.errors.treatmentDuration,
                        )}
                    />
                </Grid>
                <Grid item xs={6}>
                    <TextField
                        fullWidth
                        type="number"
                        label={t('dosageAmount')}
                        variant="outlined"
                        name="dosageAmount"
                        value={formik.values.dosageAmount}
                        onChange={formik.handleChange}
                        InputProps={{inputProps: {min: 0, step: 0.001}}}
                        helperText={
                            formik.touched.dosageAmount && formik.errors.dosageAmount
                        }
                        error={Boolean(
                            formik.touched.dosageAmount && formik.errors.dosageAmount,
                        )}
                    />
                </Grid>
                <Grid item xs={6}>
                    {hasPredefinedDosageUnits ? (
                        <TextField
                            fullWidth
                            select
                            label={t('dosageUnit')}
                            variant="outlined"
                            name="dosageUnit"
                            value={formik.values.dosageUnit}
                            onChange={formik.handleChange}
                            helperText={
                                formik.touched.dosageUnit && formik.errors.dosageUnit
                            }
                            error={Boolean(
                                formik.touched.dosageUnit && formik.errors.dosageUnit,
                            )}
                        >
                            <MenuItem value="">{t('emptyValue')}</MenuItem>
                            {medicationPackDosageUnits.map(unit => (
                                <MenuItem key={unit.unit} value={unit.unit}>
                                    {unit.unit}
                                </MenuItem>
                            ))}
                        </TextField>
                    ) : (
                        <TextField
                            fullWidth
                            type="text"
                            label={t('dosageUnit')}
                            variant="outlined"
                            name="dosageUnit"
                            value={formik.values.dosageUnit}
                            onChange={formik.handleChange}
                            helperText={
                                formik.touched.dosageUnit && formik.errors.dosageUnit
                            }
                            error={Boolean(
                                formik.touched.dosageUnit && formik.errors.dosageUnit,
                            )}
                        />
                    )}
                </Grid>
                <Grid
                    item
                    xs={formik.values.dosageFrequencyOption === 'xH' ? 6 : 12}
                >
                    <TextField
                        fullWidth
                        select
                        label={t('dosageFrequency')}
                        variant="outlined"
                        name="dosageFrequencyOption"
                        value={formik.values.dosageFrequencyOption}
                        onChange={formik.handleChange}
                        helperText={
                            formik.touched.dosageFrequencyOption &&
                            formik.errors.dosageFrequencyOption
                        }
                        error={Boolean(
                            formik.touched.dosageFrequencyOption &&
                                formik.errors.dosageFrequencyOption,
                        )}
                    >
                        <MenuItem value="">{t('emptyValue')}</MenuItem>
                        {DOSAGE_FREQUENCY_OPTIONS.map(option => (
                            <MenuItem key={option} value={option}>
                                {option === 'xH' ? t('everyXHours', {hours: 'x'}) : option}
                            </MenuItem>
                        ))}
                    </TextField>
                </Grid>
                {formik.values.dosageFrequencyOption === 'xH' && (
                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            type="number"
                            label={t('hoursCount')}
                            variant="outlined"
                            name="dosageIntervalHours"
                            value={formik.values.dosageIntervalHours}
                            onChange={formik.handleChange}
                            InputProps={{inputProps: {min: 1, step: 1}}}
                            helperText={
                                (formik.values.dosageIntervalHours !== '' ||
                                    formik.touched.dosageIntervalHours) &&
                                formik.errors.dosageIntervalHours
                            }
                            error={Boolean(
                                (formik.values.dosageIntervalHours !== '' ||
                                    formik.touched.dosageIntervalHours) &&
                                    formik.errors.dosageIntervalHours,
                            )}
                        />
                    </Grid>
                )}
                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        type="text"
                        label={t('usageRecommendationComment')}
                        variant="outlined"
                        name="usageRecommendation"
                        value={formik.values.usageRecommendation}
                        onChange={formik.handleChange}
                        helperText={
                            formik.touched.usageRecommendation &&
                            formik.errors.usageRecommendation
                        }
                        error={Boolean(
                            formik.touched.usageRecommendation &&
                                formik.errors.usageRecommendation,
                        )}
                    />
                </Grid>
            </>
        )}
        <Grid item xs={12}>
            <ReimbursementSelect
                disabled={isReimbursementDisabled}
                medicationPackReimbursements={
                    medicationPackReimbursements
                }
                paymentLevel={formik.values.paymentLevel}
                onChange={formik.handleChange}
                error={Boolean(
                    formik.touched.paymentLevel &&
                        formik.errors.paymentLevel,
                )}
                helperText={
                    formik.touched.paymentLevel &&
                    formik.errors.paymentLevel
                }
            />
        </Grid>
        <Grid item>
            {insuranceInfoComponent && insuranceInfoComponent}
        </Grid>
    </>
);

export default PrescriptionFormFields;
